/**
 * M6 — Instrumentação (input manual + lista)
 * -------------------------------------------
 * POC opera com inputs manuais. Quando a API TetraTech estiver online,
 * a UI exibe banner "leituras automáticas via TetraTech" e esconde o
 * formulário (flag em `instrumentacao.mode === 'integrated'`).
 *
 * R-A06: origem do dado auditável.
 * R-A07: vigência da carta de controle.
 * R-C08: nenhum valor numérico sem classificação visual.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardTitle, LEVEL_UI, LevelIcon } from '@/ui';
import {
  classifyReadingLevel, isInVigencia, thresholdFromInstrument,
  type InstrumentId,
} from '@/domain';
import { useDataSources } from '@/services/datasources';
import { BannerArea } from '@/components/BannerArea';

export default function M6Instrumentacao() {
  const { instrumentacao } = useDataSources();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<InstrumentId | null>(null);
  const [valueStr, setValueStr] = useState('');
  const [notes, setNotes] = useState('');

  const { data: instruments = [] } = useQuery({
    queryKey: ['instruments'],
    queryFn: () => instrumentacao.listInstruments(),
  });

  const { data: readings = [] } = useQuery({
    queryKey: ['readings', selectedId],
    queryFn: () => instrumentacao.listLatestReadings({
      instrumentId: selectedId ?? undefined,
      limit: 30,
    }),
    enabled: instruments.length > 0,
  });

  const selected = instruments.find((i) => i.id === selectedId);

  const submit = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('Selecione um instrumento');
      const value = parseFloat(valueStr.replace(',', '.'));
      if (Number.isNaN(value)) throw new Error('Valor inválido');
      return instrumentacao.submitReading({
        instrumentId: selected.id,
        value,
        origem: 'manual',
        notes: notes || undefined,
      });
    },
    onSuccess: () => {
      setValueStr('');
      setNotes('');
      qc.invalidateQueries({ queryKey: ['readings'] });
    },
  });

  // Pré-classifica enquanto o usuário digita pra dar feedback visual (R-C08).
  // GUPM-234: instrumento sem limiar nenhum não tem classificação prévia —
  // `thresholdFromInstrument` devolve null e a tela não mostra selo, em vez de
  // acender verde para qualquer número digitado.
  const valueNum = parseFloat(valueStr.replace(',', '.'));
  const threshold = selected ? thresholdFromInstrument(selected) : null;
  const previewLevel = threshold && !Number.isNaN(valueNum)
    ? classifyReadingLevel(threshold, valueNum, new Date())
    : null;
  const previewUi = previewLevel ? LEVEL_UI[previewLevel] : null;

  return (
    <div>
      <header className="mb-5">
        <h1 className="text-2xl font-black text-jf-yellow">Instrumentação</h1>
        <p className="text-jf-muted mt-1">
          Modo atual: <strong className="text-white">{instrumentacao.mode}</strong>
          {instrumentacao.pendingIntegration && (
            <> · aguardando <strong className="text-orange-300">{instrumentacao.pendingIntegration.owner}</strong></>
          )}
        </p>
        <Link to="/instrumentacao" className="inline-block mt-2 text-sm text-jf-yellow hover:underline">
          ← Voltar ao gráfico
        </Link>
      </header>

      <BannerArea modulo="M6" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Coluna esquerda: lista de instrumentos */}
        <Card>
          <CardTitle>Instrumentos</CardTitle>
          <ul className="space-y-1.5">
            {instruments.map((i) => (
              <li key={i.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(i.id)}
                  className={`w-full text-left p-2 rounded border transition ${
                    selectedId === i.id
                      ? 'border-jf-yellow bg-jf-border'
                      : 'border-jf-border bg-jf-surface2 hover:border-jf-yellow/50'
                  }`}
                >
                  <div className="font-bold text-white text-sm">{i.code}</div>
                  <div className="text-xs text-jf-muted">{i.label}</div>
                </button>
              </li>
            ))}
          </ul>
        </Card>

        {/* Coluna direita: form + leituras */}
        <div className="lg:col-span-2 space-y-4">
          {!selected ? (
            <Card>
              <CardTitle>Selecione um instrumento</CardTitle>
              <p className="text-jf-muted">
                Escolha um instrumento à esquerda pra inserir leitura manual.
              </p>
            </Card>
          ) : (
            <>
              <Card>
                <CardTitle>Nova leitura — {selected.code}</CardTitle>
                {!isInVigencia(selected, new Date()) && (
                  <div className="text-xs text-orange-300 mb-3">
                    ⚠️ Carta de Controle Walm/2025 vige a partir de{' '}
                    {selected.thresholdsValidFrom.toLocaleDateString('pt-BR')}. Leituras
                    anteriores não são classificadas (R-A07).
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label className="block">
                    <span className="text-xs text-jf-dim">Valor</span>
                    <input
                      type="text"
                      value={valueStr}
                      onChange={(e) => setValueStr(e.target.value)}
                      placeholder="ex: 803.5"
                      className="w-full mt-1 px-2 py-1.5 bg-jf-surface2 border border-jf-border rounded text-white"
                    />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="text-xs text-jf-dim">Observação (opcional)</span>
                    <input
                      type="text"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="ex: leitura após chuva intensa"
                      className="w-full mt-1 px-2 py-1.5 bg-jf-surface2 border border-jf-border rounded text-white"
                    />
                  </label>
                </div>

                {previewUi && (
                  <div
                    className="mt-3 px-3 py-2 rounded border inline-flex items-center gap-2 text-sm"
                    style={{ borderColor: previewUi.color, color: previewUi.color, background: previewUi.bg }}
                  >
                    <LevelIcon level={previewLevel!} />
                    <span className="font-bold">{previewUi.label}</span>
                    <span className="text-jf-muted ml-1 text-xs">— classificação prévia</span>
                  </div>
                )}

                <div className="flex gap-2 mt-4">
                  <Button onClick={() => submit.mutate()} disabled={submit.isPending}>
                    {submit.isPending ? 'Enviando…' : 'Registrar leitura manual'}
                  </Button>
                  {submit.error && (
                    <span className="text-sm text-level-emergencia">{(submit.error as Error).message}</span>
                  )}
                  {submit.isSuccess && (
                    <span className="text-sm text-green-400">✓ leitura registrada com origem=manual</span>
                  )}
                </div>
              </Card>

              <Card>
                <CardTitle>Últimas leituras · {selected.code}</CardTitle>
                {readings.length === 0 ? (
                  <p className="text-jf-muted text-sm">Sem leituras ainda.</p>
                ) : (
                  <ul className="space-y-1.5 text-sm">
                    {readings.map((r) => {
                      const ui = r.level ? LEVEL_UI[r.level] : null;
                      return (
                        <li key={r.id} className="flex items-center justify-between border-b border-jf-border/50 pb-1.5">
                          <div className="flex items-center gap-3">
                            <span className="text-white font-mono">{r.value.toFixed(2)}</span>
                            {ui && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1" style={{ color: ui.color, background: ui.bg }}>
                                <LevelIcon level={r.level!} size={11} /> {ui.label}
                              </span>
                            )}
                            <span className="text-[10px] uppercase tracking-wide text-jf-dim">
                              origem: {r.origem}
                            </span>
                          </div>
                          <span className="text-xs text-jf-dim">
                            {r.ts.toLocaleString('pt-BR')}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
